import { isEqual, uniqWith } from 'lodash';
import { Product } from '@sparknz/tracking';

import {
  BillingFrequency,
  ProcessContext,
} from '__generated__/shopCartFragment.graphql';
import { constructConnectionStatus } from 'journey/broadband/utils/constructConnectionStatus.utils';
import {
  CartProductDataProps,
  CartSummaryFrequency,
  TrackingCart,
  TrackingCartItem,
  TrackingCartItemPrices,
} from 'shared/types/cart';
import { getConnectionType } from './connectionType.utils';
import { GIFT_PRODUCT } from './tracking.constants';

export function getFrequencyCostSummary(
  summaries: TrackingCart['summaries'],
  frequency: BillingFrequency,
): CartSummaryFrequency {
  const costSummary = summaries.find(
    (cost) => cost.billingFrequency === frequency.toString(),
  );

  return costSummary
    ? {
        billingFrequency: costSummary.billingFrequency,
        frequencyType: costSummary.frequencyType,
        discount: costSummary.discount,
        taxExclusiveDiscount: costSummary.taxExclusiveDiscount,
        subtotal: costSummary.subtotal,
        total: costSummary.total,
        taxExclusiveSubtotal: costSummary.taxExclusiveSubtotal,
        taxExclusiveTotal: costSummary.taxExclusiveTotal,
        gst: costSummary.gst,
      }
    : {
        billingFrequency: frequency,
        frequencyType: null,
        discount: 0,
        taxExclusiveDiscount: 0,
        subtotal: 0,
        total: 0,
        taxExclusiveSubtotal: 0,
        taxExclusiveTotal: 0,
        gst: 0,
      };
}

export function buildCartProductInfo(product: TrackingCartItem) {
  return {
    productID: product.offerId,
    productName: product.title,
    sku: product.offerId,
    productImage: product.imageUrl,
    manufacturer: 'Spark',
  };
}

interface GetFrequencyCostParameter {
  itemPrices: TrackingCartItemPrices;
  frequencies: BillingFrequency[];
  isBusiness: boolean;
}

export function getFrequencyCost({
  itemPrices,
  frequencies,
  isBusiness,
}: GetFrequencyCostParameter) {
  const itemPrice = itemPrices.find((cost) =>
    frequencies.includes(cost.billingFrequency),
  );

  if (itemPrice) {
    return String(
      isBusiness
        ? itemPrice?.effectivePrice?.taxExclusiveValue
        : itemPrice?.effectivePrice?.value,
    );
  }

  return '0';
}

interface GetBasePriceParameter {
  itemPrice: TrackingCartItem['totalPrice'];
  itemPrices: TrackingCartItemPrices;
  isBusiness: boolean;
}

export function getBasePrice({
  itemPrice,
  itemPrices,
  isBusiness,
}: GetBasePriceParameter) {
  if (itemPrice) {
    return String(itemPrice);
  }

  const itemRecurringPrice = itemPrices.find(
    (cost) => cost.frequencyType === 'Recurring',
  );

  if (itemRecurringPrice) {
    return String(
      isBusiness
        ? itemRecurringPrice?.basePrice?.taxExclusiveValue
        : itemRecurringPrice?.basePrice?.value,
    );
  }

  return '0';
}

export function getItemPrices(item: TrackingCartItem, isBusiness: boolean) {
  const itemBasePrice = getBasePrice({
    itemPrice: item.totalPrice,
    itemPrices: item.prices,
    isBusiness,
  });
  const itemOneOffCost = getFrequencyCost({
    itemPrices: item.prices,
    frequencies: ['UP_FRONT', 'ONE_OFF'],
    isBusiness,
  });
  const itemRecurringCost = getFrequencyCost({
    itemPrices: item.prices,
    frequencies: ['MONTHLY'],
    isBusiness,
  });

  // Note: itemTotalBasePrice is same as itemBasePrice because we are not support an option to change an item quantity as of now.
  return {
    itemBasePrice,
    itemOneOffCost,
    itemRecurringCost,
    itemTotalBasePrice: itemBasePrice,
    itemTotalOneOffCost: itemOneOffCost,
    itemTotalRecurringCost: itemRecurringCost,
  };
}

interface BuildCommonAttributesProps {
  bundleId: string;
  product: TrackingCartItem;
  isBusiness: boolean;
  bundleType?: string;
  bundleSubType?: string;
  connectionStatus?: string;
  connectionType?: string;
  isExistingPlan?: boolean;
  processContext?: ProcessContext;
}

export function buildCommonProductAttributes({
  bundleId,
  bundleType,
  bundleSubType,
  product,
  isBusiness,
  connectionStatus,
  connectionType,
  isExistingPlan,
  processContext,
}: BuildCommonAttributesProps) {
  const isGiftProduct = product?.dataLayerCategory === GIFT_PRODUCT;

  return {
    baseStore: isBusiness ? 'businessshop' : 'personalshop',
    bundleEntryNumber: bundleId,
    bundleType: bundleType || null,
    bundleSubType: bundleSubType || null,
    connectionStatus: connectionStatus || null,
    categoryIdentifier: product.categories.join(','),
    ...getConnectionType(connectionType),
    contractTerm: product.contractTerm?.name || null,
    deferredPaymentOption: product?.contractTerm
      ? `${product?.contractTerm?.value} ${product?.contractTerm?.unit}`
      : null,
    displayName: product.title,
    isDeferredPayment: Boolean(product?.contractTerm?.value),
    isExistingPlan,
    isGiftWithPurchase: isGiftProduct,
    isGSTIncluded: !isBusiness,
    isIncludedInPlan: product.isIncluded,
    isInStock: true,
    isPreOrder: Boolean(product.preOrderable),
    paymentType: product.balanceManagement,
    promoDeals: product.promotion ? product.title : null,
    segment: isBusiness ? 'BUSINESS' : 'CONSUMER',
    isChangePlan: processContext === 'CHANGE_OFFER',
  };
}

/**
'ServicePlan' and 'ServiceAddOn' are dataLayerCategories where Digital Optimization team added in catalogue to identify 'Plans'
 The digital optimization teams (Gabe,Jackey and Lucia) aware this is workaround to achieve the analytic design.
 For more detail please refer to https://sparknz.visualstudio.com/Spark/_workitems/edit/2145055. 
*/

const PLAN_IDENTIFIERS = new Set(['ServicePlan', 'ServiceAddOn']);

export function buildServicePlanAttributes(
  items: CartProductDataProps['bundle']['items'],
) {
  const linkedPlanItem = items.find(({ dataLayerCategory }) =>
    PLAN_IDENTIFIERS.has(dataLayerCategory),
  );

  return {
    servicePlanType: linkedPlanItem?.balanceManagement ?? null,
    servicePlanName: linkedPlanItem?.title ?? null,
  };
}

export function buildCartProductData({
  bundle,
  isBusiness,
  connectionType,
  currentProvider,
  broadbandAttributes,
}: CartProductDataProps): Product[] {
  const { items } = bundle;

  // Note: first item in the array is always the primary product
  const primaryItem = items[0];
  const primaryItemPrices = getItemPrices(primaryItem, isBusiness);

  const discountInfo = bundle.items
    .map((item) => item.prices.map((price) => price.discount))
    .flat()
    .find(Boolean);

  const isExistingPlan = Boolean(bundle.lineNumber);
  const linkedProducts = items
    .filter((item) => {
      if (item.offerId === primaryItem.offerId) {
        return false;
      }

      if (item.shouldHide) {
        return false;
      }

      return true;
    })
    .map((linkedItem) => {
      const itemBilling = getItemPrices(linkedItem, isBusiness);
      return {
        productInfo: buildCartProductInfo(linkedItem),
        category: {
          primaryCategory: linkedItem.dataLayerCategory,
          subCategory1: linkedItem.categories?.[1],
        },
        attributes: {
          ...buildCommonProductAttributes({
            product: linkedItem,
            isBusiness,
            isExistingPlan,
            bundleId: bundle.bundleId,
            bundleType: bundle.bundleType,
            bundleSubType: bundle.bundleSubType,
            connectionType,
            processContext: bundle?.processContext as ProcessContext,
          }),
          ...itemBilling,
          currentProvider: currentProvider || null,
        },
      };
    });

  return [
    {
      productInfo: buildCartProductInfo(primaryItem),
      category: {
        primaryCategory: primaryItem.dataLayerCategory,
      },
      attributes: {
        ...buildCommonProductAttributes({
          product: primaryItem,
          isBusiness,
          isExistingPlan,
          bundleId: bundle.bundleId,
          bundleType: bundle.bundleType,
          bundleSubType: bundle.bundleSubType,
          connectionStatus: constructConnectionStatus(
            bundle.availability ? [bundle.availability] : [],
          ),
          connectionType,
          processContext: bundle?.processContext as ProcessContext,
        }),
        ...buildServicePlanAttributes(items),
        baseStore: isBusiness ? 'businessshop' : 'personalshop',
        categoryIdentifier: primaryItem.categories.join(','),
        ...getConnectionType(connectionType),
        currentProvider: currentProvider || null,
        contractTerm: primaryItem.contractTerm?.name || null,
        isGSTIncluded: !isBusiness,
        paymentType: primaryItem.balanceManagement,
        segment: isBusiness ? 'BUSINESS' : 'CONSUMER',
        discountInfo: [
          {
            id: discountInfo?.discountId || null,
            type: discountInfo?.type || null,
            discountValue: discountInfo?.discountAppliedValue || null,
            discountAction: discountInfo?.discountType || null,
          },
        ],
        ...primaryItemPrices,
        ...broadbandAttributes,
      },
      quantity: `${primaryItem.quantity}`,
      linkedProduct: linkedProducts,
    },
  ];
}

export function buildDiscountData(productDataItem: Product[]) {
  return uniqWith(
    productDataItem.map((item) => item.attributes.discountInfo).flat(),
    isEqual,
  );
}
